import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card, Table, Tag, Button, Space, Modal, Form, InputNumber, Input, Select,
  Alert, Typography, Tooltip, Segmented, Empty
} from 'antd';
import {
  PlusOutlined, DownloadOutlined, SwapOutlined, RollbackOutlined,
  KeyOutlined, StopOutlined, CopyOutlined, ReloadOutlined
} from '@ant-design/icons';
import { internalScanDevices, internalAccounts } from '../../api/internalAdminEndpoints';
import { useAppNotifications } from '../../utils/notify';
import PageHeader from '../../components/PageHeader';
import useInternalAdminAuth from '../../contexts/InternalAdminAuthContext';

const { Text, Paragraph } = Typography;

// Stage SCAN-1 — ESP32 QR scanner hardware inventory.
//
// This is the Glass-i-side half of the two-level ownership model:
//   ① manufacture — register / bulk-register mints a device_id + key
//   ② allocation  — lend a keyed box to a tenant, reclaim it, retire it
// The tenant's master user owns ③ (department + entry/exit role) on their own
// screen. Nothing here can assign a department, by design.
//
// THE KEY IS SHOWN ONCE. It is held only in the modal's state and is dropped
// the moment the modal closes. There is no "show key again" — recovery means
// rotating the key and re-flashing the device, because a firmware-hardcoded
// key cannot be rotated over the air.

const STATUS_TAGS = {
  IN_STOCK:  { color: 'default',    label: 'In stock' },
  ALLOCATED: { color: 'green',      label: 'Allocated' },
  RETURNED:  { color: 'orange',     label: 'Returned' },
  RETIRED:   { color: 'red',        label: 'Retired' }
};

const FILTERS = [
  { value: '',          label: 'All' },
  { value: 'IN_STOCK',  label: 'In stock' },
  { value: 'ALLOCATED', label: 'Allocated' },
  { value: 'RETURNED',  label: 'Returned' },
  { value: 'RETIRED',   label: 'Retired' }
];

// "4s" / "11m" / "2d" — a scanner's silence is the first sign of trouble, so
// it needs to read at a glance rather than as a timestamp.
function sinceLabel(iso) {
  if (!iso) return '—';
  const secs = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h`;
  return `${Math.floor(secs / 86400)}d`;
}

function isOnline(iso) {
  // Heartbeat is a 5-minute poll, so anything inside ~11 minutes has not
  // missed two consecutive beats yet.
  return !!iso && (Date.now() - new Date(iso).getTime()) < 11 * 60 * 1000;
}

export default function ScanDeviceInventoryPage() {
  const { hasPermission } = useInternalAdminAuth();
  const { notifySuccess, notifyError } = useAppNotifications();

  const canManage = hasPermission('scan_devices.manage');
  const canAllocate = hasPermission('scan_devices.allocate');

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');

  const [tenants, setTenants] = useState([]);
  const [tenantsLoaded, setTenantsLoaded] = useState(false);

  const [registerOpen, setRegisterOpen] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [registerForm] = Form.useForm();

  const [allocateFor, setAllocateFor] = useState(null);
  const [allocating, setAllocating] = useState(false);
  const [allocateForm] = Form.useForm();

  // Raw keys live ONLY here, only while the modal is open.
  const [mintedKeys, setMintedKeys] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (statusFilter) params.allocationStatus = statusFilter;
      if (search.trim()) params.search = search.trim();
      const data = await internalScanDevices.list(params);
      setRows(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      notifyError(err, 'Could not load the scanner inventory');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search, notifyError]);

  useEffect(() => { load(); }, [load]);

  // Tenant picker for allocation. Loaded lazily — only an allocation needs it.
  //
  // PAGED, not one big fetch: /api/internal/accounts caps `limit` at 200, and
  // a server is planned to carry 250 tenants. Asking for more is a hard 400,
  // and asking for exactly 200 would silently truncate the list so an operator
  // could not find a real account. The loop is bounded so a paging bug cannot
  // turn into an endless request storm.
  const loadTenants = useCallback(async () => {
    if (tenantsLoaded) return;
    const PAGE = 200;
    const MAX_PAGES = 25;
    try {
      const all = [];
      for (let page = 0; page < MAX_PAGES; page++) {
        // eslint-disable-next-line no-await-in-loop
        const data = await internalAccounts.list({
          status: 'active', limit: PAGE, offset: page * PAGE
        });
        const items = Array.isArray(data?.items) ? data.items : [];
        all.push(...items);
        if (items.length < PAGE) break;   // short page = last page
      }
      setTenants(all);
      setTenantsLoaded(true);
    } catch (err) {
      notifyError(err, 'Could not load the account list');
    }
  }, [tenantsLoaded, notifyError]);

  const tenantOptions = useMemo(
    () => tenants.map((t) => ({
      value: t.tenantId,
      label: t.displayName || t.legalName || t.tenantId
    })),
    [tenants]
  );

  // ---- actions ------------------------------------------------------

  async function doRegister() {
    let values;
    try { values = await registerForm.validateFields(); } catch { return; }
    setRegistering(true);
    try {
      const count = Number(values.count) || 1;
      const body = { count, hardwareRev: values.hardwareRev || null };
      const data = count === 1
        ? { devices: [await internalScanDevices.register(body)], count: 1 }
        : await internalScanDevices.registerBulk(body);
      setMintedKeys(data.devices || []);
      setRegisterOpen(false);
      registerForm.resetFields();
      notifySuccess(`Registered ${data.count ?? data.devices.length} scanner(s).`);
      load();
    } catch (err) {
      notifyError(err, 'Could not register the scanners');
    } finally {
      setRegistering(false);
    }
  }

  async function doAllocate() {
    let values;
    try { values = await allocateForm.validateFields(); } catch { return; }
    setAllocating(true);
    try {
      await internalScanDevices.allocate(allocateFor.id, { tenantId: values.tenantId });
      notifySuccess(
        `${allocateFor.deviceId} allocated. Their master user must now assign it to a department.`
      );
      setAllocateFor(null);
      allocateForm.resetFields();
      load();
    } catch (err) {
      notifyError(err, 'Could not allocate the scanner');
    } finally {
      setAllocating(false);
    }
  }

  function confirmDeallocate(row) {
    Modal.confirm({
      title: `Reclaim ${row.deviceId}?`,
      okText: 'Reclaim',
      content: (
        <div>
          <Paragraph style={{ marginBottom: 8 }}>
            Returns the scanner to Glass-i stock and <b>clears its department, alias and
            operator</b>, so it cannot carry {row.tenantName || 'this account'}&apos;s
            configuration into its next deployment.
          </Paragraph>
          <Text type="secondary">The device key is unchanged — no re-flash needed.</Text>
        </div>
      ),
      onOk: async () => {
        try {
          await internalScanDevices.deallocate(row.id);
          notifySuccess(`${row.deviceId} returned to stock.`);
          load();
        } catch (err) {
          notifyError(err, 'Could not reclaim the scanner');
        }
      }
    });
  }

  function confirmRotate(row) {
    Modal.confirm({
      title: `Rotate the key for ${row.deviceId}?`,
      okText: 'Rotate key',
      okButtonProps: { danger: true },
      content: (
        <Alert
          type="warning"
          showIcon
          message="This scanner will stop working immediately"
          description={
            'A firmware-hardcoded key cannot be rotated over the air. The device stays offline '
            + 'until it is physically re-flashed with the new key. Only do this if the current '
            + 'key is lost or compromised.'
          }
        />
      ),
      onOk: async () => {
        try {
          const data = await internalScanDevices.rotateKey(row.id);
          setMintedKeys([{ deviceId: data.deviceId, deviceKey: data.deviceKey }]);
          load();
        } catch (err) {
          notifyError(err, 'Could not rotate the key');
        }
      }
    });
  }

  function confirmRetire(row) {
    Modal.confirm({
      title: `Retire ${row.deviceId}?`,
      okText: 'Retire',
      okButtonProps: { danger: true },
      content: 'Ends this unit\'s service life. Its key is revoked permanently and the serial '
             + 'is never reissued. This cannot be undone.',
      onOk: async () => {
        try {
          await internalScanDevices.retire(row.id);
          notifySuccess(`${row.deviceId} retired.`);
          load();
        } catch (err) {
          notifyError(err, 'Could not retire the scanner');
        }
      }
    });
  }

  function downloadCsv() {
    if (!mintedKeys?.length) return;
    const csv = ['device_id,device_key']
      .concat(mintedKeys.map((d) => `${d.deviceId},${d.deviceKey}`))
      .join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `scan-devices-${mintedKeys[0].deviceId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // ---- columns ------------------------------------------------------

  const columns = [
    {
      title: 'Device', dataIndex: 'deviceId', width: 130, fixed: 'left',
      render: (v, r) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ fontFamily: 'monospace' }}>{v}</Text>
          {r.displayName ? <Text type="secondary" style={{ fontSize: 12 }}>{r.displayName}</Text> : null}
        </Space>
      )
    },
    {
      title: 'Status', dataIndex: 'allocationStatus', width: 110,
      render: (v) => {
        const t = STATUS_TAGS[v] || { color: 'default', label: v };
        return <Tag color={t.color}>{t.label}</Tag>;
      }
    },
    {
      title: 'Account', dataIndex: 'tenantName', width: 190,
      render: (v) => v || <Text type="secondary">—</Text>
    },
    {
      title: 'Department', dataIndex: 'departmentCode', width: 150,
      render: (v, r) => {
        if (r.allocationStatus !== 'ALLOCATED') return <Text type="secondary">—</Text>;
        // The gap that stops a box scanning. Worth calling out here, because
        // Glass-i support gets the call before the tenant notices.
        if (!v) return <Tag color="warning">Awaiting their master</Tag>;
        return <Space size={4}><Text>{v}</Text><Tag>{r.scanRole === 'STAGE_IN' ? 'ENTRY' : 'EXIT'}</Tag></Space>;
      }
    },
    {
      title: 'Last seen', dataIndex: 'lastSeenAt', width: 110,
      render: (v) => (
        <Space size={6}>
          <span style={{
            display: 'inline-block', width: 7, height: 7, borderRadius: '50%',
            background: isOnline(v) ? '#52c41a' : 'transparent',
            border: isOnline(v) ? 'none' : '1.5px solid #bfbfbf'
          }} />
          <Text type={isOnline(v) ? undefined : 'secondary'}>{sinceLabel(v)}</Text>
        </Space>
      )
    },
    {
      title: 'Hardware', dataIndex: 'hardwareRev', width: 150,
      render: (v, r) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: 12 }}>{v || '—'}</Text>
          {r.firmwareVersion
            ? <Text type="secondary" style={{ fontSize: 11 }}>fw {r.firmwareVersion}</Text>
            : null}
        </Space>
      )
    },
    {
      title: 'Health', key: 'health', width: 130,
      render: (_, r) => {
        if (r.revokedAt) return <Tag color="red">Revoked</Tag>;
        if (r.lockedUntil && new Date(r.lockedUntil) > new Date()) {
          return (
            <Tooltip title="Locked after repeated bad keys. Clears automatically, or on a key rotation.">
              <Tag color="volcano">Locked out</Tag>
            </Tooltip>
          );
        }
        if (!r.isActive) {
          return (
            <Tooltip title="Switched off by the tenant's master user. Glass-i does not override this.">
              <Tag color="orange">Deactivated</Tag>
            </Tooltip>
          );
        }
        return <Tag color="green">OK</Tag>;
      }
    },
    {
      title: 'Actions', key: 'actions', width: 250, fixed: 'right',
      render: (_, r) => (
        <Space size={4} wrap>
          {canAllocate && ['IN_STOCK', 'RETURNED'].includes(r.allocationStatus) ? (
            <Button size="small" icon={<SwapOutlined />}
              onClick={() => { loadTenants(); setAllocateFor(r); }}>
              Allocate
            </Button>
          ) : null}
          {canAllocate && r.allocationStatus === 'ALLOCATED' ? (
            <Button size="small" icon={<RollbackOutlined />} onClick={() => confirmDeallocate(r)}>
              Reclaim
            </Button>
          ) : null}
          {canManage && r.allocationStatus !== 'RETIRED' ? (
            <>
              <Tooltip title="Requires a physical re-flash">
                <Button size="small" icon={<KeyOutlined />} onClick={() => confirmRotate(r)} />
              </Tooltip>
              <Tooltip title="Retire this unit permanently">
                <Button size="small" danger icon={<StopOutlined />} onClick={() => confirmRetire(r)} />
              </Tooltip>
            </>
          ) : null}
        </Space>
      )
    }
  ];

  return (
    <div>
      <PageHeader
        title="Scanner Inventory"
        subtitle="ESP32 QR scanners — manufacture, allocation and service"
        actions={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading} />
            {canManage ? (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setRegisterOpen(true)}>
                Register scanners
              </Button>
            ) : null}
          </Space>
        }
      />

      <Card
        size="small"
        style={{ marginBottom: 12 }}
        styles={{ body: { display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' } }}
      >
        <Segmented
          options={FILTERS}
          value={statusFilter}
          onChange={setStatusFilter}
        />
        <Input.Search
          allowClear
          placeholder="Device ID, alias or IMEI"
          style={{ maxWidth: 300 }}
          onSearch={setSearch}
          onChange={(e) => { if (!e.target.value) setSearch(''); }}
        />
      </Card>

      <Card size="small">
        <Table
          rowKey="id"
          size="small"
          loading={loading}
          columns={columns}
          dataSource={rows}
          scroll={{ x: 1250 }}
          pagination={{ pageSize: 25, showSizeChanger: true, showTotal: (t) => `${t} scanners` }}
          locale={{
            emptyText: (
              <Empty description={
                statusFilter || search
                  ? 'No scanners match this filter.'
                  : 'No scanners registered yet. Register a batch to mint device IDs and keys.'
              } />
            )
          }}
        />
      </Card>

      {/* ---- register ------------------------------------------------ */}
      <Modal
        title="Register scanners"
        open={registerOpen}
        onCancel={() => setRegisterOpen(false)}
        onOk={doRegister}
        okText="Register"
        confirmLoading={registering}
        destroyOnClose
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Keys are shown once"
          description={
            'Device IDs are assigned in sequence (SC00001, SC00002, …). Each unit gets its own '
            + 'key, shown once on the next screen. Export the CSV and take it to the flashing '
            + 'bench — a lost key can only be recovered by rotating it and re-flashing.'
          }
        />
        <Form form={registerForm} layout="vertical" initialValues={{ count: 1 }}>
          <Form.Item
            name="count" label="How many units?"
            rules={[{ required: true, message: 'Enter a quantity' }]}
          >
            <InputNumber min={1} max={500} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="hardwareRev" label="Hardware revision (optional)">
            <Input placeholder="e.g. ESP32-S3 / EC200U rev B" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ---- the one-time key reveal --------------------------------- */}
      <Modal
        title="Device keys — copy them now"
        open={!!mintedKeys}
        onCancel={() => setMintedKeys(null)}
        width={620}
        footer={[
          <Button key="csv" type="primary" icon={<DownloadOutlined />} onClick={downloadCsv}>
            Download CSV for the flashing bench
          </Button>,
          <Button key="close" onClick={() => setMintedKeys(null)}>Done</Button>
        ]}
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="This is the only time these keys are shown"
          description={
            'Glass-i stores only a one-way hash. Nobody — including you — can retrieve a key '
            + 'later. If one is lost, the only recovery is rotating it and re-flashing the device.'
          }
        />
        <Table
          rowKey="deviceId"
          size="small"
          pagination={false}
          scroll={{ y: 280 }}
          dataSource={mintedKeys || []}
          columns={[
            { title: 'Device ID', dataIndex: 'deviceId', width: 110,
              render: (v) => <Text strong style={{ fontFamily: 'monospace' }}>{v}</Text> },
            { title: 'Device key', dataIndex: 'deviceKey',
              render: (v) => (
                <Space size={4}>
                  <Text code copyable={{ icon: <CopyOutlined /> }}
                    style={{ fontSize: 11, wordBreak: 'break-all' }}>{v}</Text>
                </Space>
              ) }
          ]}
        />
      </Modal>

      {/* ---- allocate ------------------------------------------------ */}
      <Modal
        title={allocateFor ? `Allocate ${allocateFor.deviceId}` : 'Allocate'}
        open={!!allocateFor}
        onCancel={() => setAllocateFor(null)}
        onOk={doAllocate}
        okText="Allocate"
        confirmLoading={allocating}
        destroyOnClose
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Their master user finishes the setup"
          description={
            'Allocation hands the box to the account. It still will not scan until their master '
            + 'user assigns it to a department and chooses whether it is an entry or exit '
            + 'scanner. Until then it reports DEVICE_DEPARTMENT_UNASSIGNED.'
          }
        />
        <Form form={allocateForm} layout="vertical">
          <Form.Item
            name="tenantId" label="Account"
            rules={[{ required: true, message: 'Pick an account' }]}
          >
            <Select
              showSearch
              placeholder="Search accounts"
              options={tenantOptions}
              optionFilterProp="label"
              notFoundContent={tenantsLoaded ? 'No active accounts' : 'Loading…'}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
